import { useNodes, useReactFlow, useViewport } from '@xyflow/react';
import {
  Minus as ZoomMinusIcon,
  Plus as ZoomPlusIcon,
  Minimize,
} from '@sweep-io/sweep-design/dist/icons';
import { Typography, colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import { Box, Divider, styled } from '@mui/material';
import { useSweepCanvasFitView } from '../useSweepCanvasFitView';
import { useSweepCanvasPropsCtx } from '../internal-context/SweepCanvasPropsCtx';
import { useState } from 'react';
import { useForwardPinchToSweepCanvas } from '../hooks/useForwardPinchToSweepCanvas';
import { CSS_Z_INDEX_CANVAS_CONTROLS } from '../const/zIndex';

export interface SweepCanvasControlsProps {
  noFitView?: boolean;
}

const StyledBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.white,
  padding: '12px 8px',
  border: `1px solid ${colors.grey[300]}`,
  borderRadius: '12px',
  boxShadow: '0px 2px 25px rgba(0, 0, 0, 0.1)',
  width: '54px',
});

export const CanvasControls = () => {
  const { noFitView } = useSweepCanvasPropsCtx();
  const [canvasControlsRef, setCanvasControlsRef] = useState<HTMLDivElement | null>(null);

  const reactFlowInstance = useReactFlow();
  const { zoom } = useViewport();
  const { zoomTo } = reactFlowInstance;
  const { fitView } = useSweepCanvasFitView();
  const nodes = useNodes();
  const disableFitViewButton = nodes.length === 0;
  const duration = 400;

  useForwardPinchToSweepCanvas({ element: canvasControlsRef });

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '16px',
        right: '16px',
        zIndex: CSS_Z_INDEX_CANVAS_CONTROLS,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
      }}
      ref={setCanvasControlsRef}
    >
      <StyledBox>
        <Typography color={colors.grey[800]} variant="body">
          {Math.round(zoom * 100)}%
        </Typography>

        <Box component="span" data-testid="zoom-out-button">
          <IconButton
            variant="flat"
            size="tiny"
            onClick={() => {
              if (zoom <= 0.25) {
                zoomTo(0.1, { duration });
              } else {
                zoomTo(zoom - 0.25, { duration });
              }
            }}
          >
            <ZoomMinusIcon size="medium" />
          </IconButton>
        </Box>

        <IconButton
          variant="flat"
          size="tiny"
          onClick={() => {
            if (zoom <= 0.1) {
              zoomTo(0.25, { duration });
            } else {
              zoomTo(zoom + 0.25, { duration });
            }
          }}
          data-testid="zoom-in-button"
        >
          <ZoomPlusIcon size="medium" />
        </IconButton>

        {!noFitView && (
          <>
            <Divider sx={{ width: '100%', my: 1 }} />

            <Tooltip title="Fit view" placement="left">
              <Box component="span" data-testid="fit-view-button">
                <IconButton
                  variant="flat"
                  size="tiny"
                  disabled={disableFitViewButton}
                  onClick={() => {
                    fitView({ duration: 400 });
                  }}
                >
                  <Minimize />
                </IconButton>
              </Box>
            </Tooltip>
          </>
        )}
      </StyledBox>
    </Box>
  );
};
